import React, { FC, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Card, Popconfirm, message } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { FieldTechHomeAddress } from '../../utils/WrenchModeEventUtils';
import {
	deleteFieldTechHomeAddress,
	fetchFieldTechHomeAddresses,
} from '../../thunks/field_techs_thunks';
import useMountEffect from '../../hooks/useMountEffect';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { AddressDisplay } from '../address_display/AddressDisplay';
import EditFieldTechHomeAddressModal from './EditFieldTechHomeAddressModal';

interface FieldTechHomeAddressesCardProps {
	fieldTechEmail: string;
	fetchHomeAddresses: (fieldTechEmail: string) => Promise<FieldTechHomeAddress[]>;
	deleteHomeAddress: (homeAddressId: number) => Promise<unknown>;
}

const FieldTechHomeAddressesCard: FC<FieldTechHomeAddressesCardProps> = ({
	fieldTechEmail,
	fetchHomeAddresses,
	deleteHomeAddress,
}): React.ReactElement => {
	const [homeAddresses, setHomeAddresses] = useState<FieldTechHomeAddress[]>([]);
	const [loading, setLoading] = useState(true);
	const [loadFailed, setLoadFailed] = useState(false);
	const [showEdit, setShowEdit] = useState(false);
	const [editingHomeAddress, setEditingHomeAddress] = useState<FieldTechHomeAddress | null>(null);
	const [deletingHomeAddressId, setDeletingHomeAddressId] = useState<number | null>(null);

	const loadHomeAddresses = () =>
		fetchHomeAddresses(fieldTechEmail)
			.then((records) => {
				setHomeAddresses(records || []);
				setLoadFailed(false);
			})
			.catch(() => setLoadFailed(true))
			.finally(() => setLoading(false));

	useMountEffect(() => {
		loadHomeAddresses();
	});

	const openAdd = () => {
		setEditingHomeAddress(null);
		setShowEdit(true);
	};

	const openEdit = (homeAddress: FieldTechHomeAddress) => () => {
		setEditingHomeAddress(homeAddress);
		setShowEdit(true);
	};

	const closeEdit = () => setShowEdit(false);

	const handleEditSuccess = () => {
		setShowEdit(false);
		loadHomeAddresses();
	};

	const handleDelete = (homeAddress: FieldTechHomeAddress) => () => {
		setDeletingHomeAddressId(homeAddress.id);
		deleteHomeAddress(homeAddress.id)
			.then(loadHomeAddresses)
			.catch((error) =>
				message.error(typeof error === 'string' ? error : 'Unable to delete home address.')
			)
			.finally(() => setDeletingHomeAddressId(null));
	};

	const renderHomeAddressRow = (homeAddress: FieldTechHomeAddress) => (
		<div
			key={`home-address-${homeAddress.id}`}
			className="flex items-start justify-between gap-2 py-1"
		>
			<div className="min-w-0 flex-1">
				<div className="bodymedium text-character-primary">{homeAddress.name}</div>
				<AddressDisplay
					className="bodyregular text-character-secondary"
					address={homeAddress.address}
				/>
			</div>
			<AccessPermissionChecker
				name={PERMISSION_NAMES.MODIFY_FIELD_TECH_DETAILS}
				permissionDeniedContent={null}
			>
				<div className="flex shrink-0 items-center">
					<Button
						type="text"
						size="small"
						icon={<EditOutlined />}
						aria-label="Edit home address"
						onClick={openEdit(homeAddress)}
					/>
					<Popconfirm
						title="Delete this home address?"
						okText="Delete"
						okButtonProps={{ danger: true }}
						cancelText="Cancel"
						onConfirm={handleDelete(homeAddress)}
					>
						<Button
							type="text"
							size="small"
							icon={<DeleteOutlined />}
							aria-label="Delete home address"
							loading={deletingHomeAddressId === homeAddress.id}
						/>
					</Popconfirm>
				</div>
			</AccessPermissionChecker>
		</div>
	);

	const renderBody = () => {
		if (loadFailed) {
			return 'Unable to load home addresses.';
		}
		if (homeAddresses.length === 0) {
			return 'No Home Addresses';
		}
		return <div>{homeAddresses.map(renderHomeAddressRow)}</div>;
	};

	return (
		<div className="rowSpacing">
			<Card
				title="Home Addresses"
				loading={loading}
				extra={
					<AccessPermissionChecker
						name={PERMISSION_NAMES.MODIFY_FIELD_TECH_DETAILS}
						permissionDeniedContent={null}
					>
						<Button type="link" onClick={openAdd}>
							Add
						</Button>
					</AccessPermissionChecker>
				}
			>
				{renderBody()}
			</Card>
			{showEdit && (
				<EditFieldTechHomeAddressModal
					fieldTechEmail={fieldTechEmail}
					homeAddress={editingHomeAddress}
					onCancel={closeEdit}
					onSuccess={handleEditSuccess}
				/>
			)}
		</div>
	);
};

const mapDispatchToProps = (dispatch) => ({
	fetchHomeAddresses: (fieldTechEmail: string) =>
		dispatch(fetchFieldTechHomeAddresses(ROLE_TYPES.SUPPLIER)(fieldTechEmail)),
	deleteHomeAddress: (homeAddressId: number) =>
		dispatch(deleteFieldTechHomeAddress(ROLE_TYPES.SUPPLIER)(homeAddressId)),
});

export default connect(null, mapDispatchToProps)(FieldTechHomeAddressesCard);
